import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, DollarSign, TrendingUp, Package, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [distributing, setDistributing] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDistributeROI = async () => {
    setDistributing(true);
    try {
      const response = await adminApi.distributeROI();
      toast.success(response.data.message);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to distribute ROI');
    } finally {
      setDistributing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { 
      label: 'Total Users', 
      value: stats?.total_users || 0, 
      subtext: `${stats?.active_users || 0} active`,
      icon: Users,
      color: 'border-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    { 
      label: 'Total Deposited', 
      value: `$${(stats?.total_deposited || 0).toLocaleString()}`, 
      subtext: 'All time',
      icon: ArrowDown,
      color: 'border-green-500',
      bgColor: 'bg-green-500/10'
    },
    { 
      label: 'Total Withdrawn', 
      value: `$${(stats?.total_withdrawn || 0).toLocaleString()}`, 
      subtext: 'All time',
      icon: ArrowUp,
      color: 'border-red-500',
      bgColor: 'bg-red-500/10'
    },
    { 
      label: 'Total Invested', 
      value: `$${(stats?.total_invested || 0).toLocaleString()}`, 
      subtext: 'In packages',
      icon: Package,
      color: 'border-amber-500',
      bgColor: 'bg-amber-500/10'
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-overview">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className={`stat-card border-l-2 ${stat.color}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">{stat.label}</p>
                <p className="text-2xl text-white font-medium mt-1">{stat.value}</p>
                <p className="text-xs text-zinc-400 mt-2">{stat.subtext}</p>
              </div>
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className="w-4 h-4 text-zinc-400" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Pending Approvals */}
      <div className="grid md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-6"
        >
          <h2 className="text-white font-medium mb-4">Pending Approvals</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-black/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <ArrowDown className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Pending Deposits</p>
                  <p className="text-xs text-zinc-500">Awaiting approval</p>
                </div>
              </div>
              <span className="text-2xl text-amber-400 font-bold">
                {stats?.pending_deposits || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-black/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <ArrowUp className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Pending Withdrawals</p>
                  <p className="text-xs text-zinc-500">Awaiting approval</p>
                </div>
              </div>
              <span className="text-2xl text-blue-400 font-bold">
                {stats?.pending_withdrawals || 0}
              </span>
            </div>
          </div>
        </motion.div>

        {/* ROI Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-6"
        >
          <h2 className="text-white font-medium mb-4">Daily Operations</h2>
          <div className="p-4 rounded-lg bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-white font-medium">ROI Distribution</p>
                <p className="text-xs text-zinc-500">Distribute daily rewards to all active participations</p>
              </div>
            </div>
            <Button
              onClick={handleDistributeROI}
              disabled={distributing}
              className="w-full gold-btn"
              data-testid="distribute-roi-btn"
            >
              {distributing ? (
                <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Distribute Daily ROI
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="glass-card p-6"
      >
        <h2 className="text-white font-medium mb-4">Platform Health</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 rounded-lg bg-black/30">
            <p className="text-3xl text-white font-bold">{stats?.active_users || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">Active Users</p>
          </div>
          <div className="text-center p-4 rounded-lg bg-black/30">
            <p className="text-3xl text-green-400 font-bold">
              ${((stats?.total_deposited || 0) - (stats?.total_withdrawn || 0)).toLocaleString()}
            </p>
            <p className="text-xs text-zinc-500 mt-1">Net Deposits</p>
          </div>
          <div className="text-center p-4 rounded-lg bg-black/30">
            <p className="text-3xl text-amber-400 font-bold">
              {stats?.pending_deposits + stats?.pending_withdrawals || 0}
            </p>
            <p className="text-xs text-zinc-500 mt-1">Pending Actions</p>
          </div>
          <div className="text-center p-4 rounded-lg bg-black/30">
            <p className="text-3xl text-purple-400 font-bold">
              ${(stats?.total_invested || 0).toLocaleString()}
            </p>
            <p className="text-xs text-zinc-500 mt-1">Active Participations</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
